import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, Input, Card, Button, notification, Alert } from 'antd';
import UserStore from 'stores/UserStore';
import ErrorRender from 'components/shared/ErrorRender';
import { FormErrorParser } from '@partsbadger/library';

interface IProps {
    uid?: string;
    token?: string;
    email?: string;
    cardTitle?: string;
    formTestId?: string;
    panelTestId?: string;
}

const PasswordReset: React.FC<IProps> = ({ ...props }) => {
    const [errors, setErrors] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);

    const history = useHistory();

    const [form] = Form.useForm();

    const handleSubmit = async (values: any) => {
        setLoading(true);
        if (props.uid && props.token) {
            const payload = {
                uid: values.uid,
                token: values.token,
                new_password1: values.password1,
                new_password2: values.password2,
            };

            UserStore.restPasswordConfirm(payload)
                .then(resp => {
                    UserStore.login(values.username, values.password1).then(data => {
                        history.push('/my-account');
                        setLoading(false);
                    });
                })
                .catch(err => {
                    setErrors(err);
                    setLoading(false);
                });
        } else {
            const payload = {
                old_password: values.old_password,
                new_password1: values.password1,
                new_password2: values.password2,
            };
            UserStore.changePassword(payload)
                .then(resp => {
                    const args = {
                        message: 'Your password was changed successfully',
                    };
                    notification.success(args);
                    history.push('/my-account');
                    setLoading(false);
                })
                .catch(err => {
                    setErrors(err);
                    setLoading(false);
                });
        }
    };

    return (
        <Card title={props.cardTitle || 'Change Password'} bordered data-testid={props.panelTestId}>
            {errors ? <ErrorRender error={errors} /> : null}

            {props.uid && props.token ? (
                <Form
                    form={form}
                    onFinish={handleSubmit}
                    layout="vertical"
                    data-testid={props.formTestId}
                    onFinishFailed={errorInfo => {
                        notification.error({
                            key: 'form-errors',
                            duration: 8,
                            message: 'Please input all required fields',
                            description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                            placement: 'top',
                            maxCount: 1,
                        });
                    }}
                >
                    <Form.Item
                        name="uid"
                        style={{ display: 'none' }}
                        rules={[
                            {
                                required: true,
                                message: 'Please input your password',
                            },
                        ]}
                        initialValue={props.uid}
                    >
                        <Input autoComplete={'off'} />
                    </Form.Item>
                    <Form.Item
                        name="token"
                        style={{ display: 'none' }}
                        rules={[
                            {
                                required: true,
                                message: 'Please input your password',
                            },
                        ]}
                        initialValue={props.token}
                    >
                        <Input autoComplete={'off'} />
                    </Form.Item>
                    <Form.Item
                        name="username"
                        rules={[
                            {
                                required: true,
                                message: 'Please input an email',
                            },
                        ]}
                        initialValue={props.email}
                    >
                        <Input disabled={true} autoComplete={'username'} />
                    </Form.Item>
                    <Form.Item
                        name="password1"
                        label={'Password'}
                        rules={[
                            {
                                required: true,
                                message: 'Please input the new password',
                            },
                        ]}
                    >
                        <Input type={'password'} autoComplete={'off'} />
                    </Form.Item>
                    <Form.Item
                        name="password2"
                        label={'Confirm Password'}
                        rules={[
                            {
                                required: true,
                                message: 'Please confirm the password',
                            },
                        ]}
                    >
                        <Input type={'password'} autoComplete={'off'} />
                    </Form.Item>
                    <Form.Item>
                        <Button block htmlType="submit" type="primary" loading={loading}>
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            ) : (
                <Form
                    onFinish={handleSubmit}
                    layout="vertical"
                    style={{ maxWidth: '400px' }}
                    data-testid={props.formTestId}
                    onFinishFailed={errorInfo => {
                        notification.error({
                            key: 'form-errors',
                            duration: 8,
                            message: 'Please input all required fields',
                            description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                            placement: 'top',
                            maxCount: 1,
                        });
                    }}
                >
                    <Form.Item
                        name="old_password"
                        label={'Current Password'}
                        rules={[
                            {
                                required: true,
                                message: 'Please input the current password',
                            },
                        ]}
                    >
                        <Input.Password autoComplete={'password'} />
                    </Form.Item>
                    <Form.Item
                        name="password1"
                        label={'New Password'}
                        rules={[
                            {
                                required: true,
                                message: 'Please input the new password',
                            },
                        ]}
                    >
                        <Input.Password autoComplete={'off'} />
                    </Form.Item>
                    <Form.Item
                        name="password2"
                        label={'Confirm New Password'}
                        rules={[
                            {
                                required: true,
                                message: 'Please confirm the password',
                            },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password1') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Passwords do not match'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password autoComplete={'off'} />
                    </Form.Item>
                    <Form.Item>
                        <Button block htmlType="submit" type="primary" loading={loading}>
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            )}
        </Card>
    );
};

export default PasswordReset;
